/**
 * ResilientUploader.js (V2 - GCS Native Resumable)
 * Features: Direct-to-GCS, Resumable Upload Protocol, Mobile Stability.
 */

const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB chunks for mobile 4G stability
const MAX_RETRIES = 5;

import { FINAL_API_URL } from '../config';
const API_URL = FINAL_API_URL.includes('/api') ? FINAL_API_URL : `${FINAL_API_URL}/api`;

class ResilientUploader {
  constructor(file, options = {}) {
    this.file = file;
    this.type = options.type || 'coach'; // 'coach', 'mi_analysis' or 'sensi'
    this.game = options.game || 'pubg_mobile';  // Canonical fallback, backend still accepts legacy aliases
    this.language = options.language || options.locale || 'pt-BR';
    this.context = options.context || '';
    this.onProgress = options.onProgress || (() => {});
    this.onStatus = options.onStatus || (() => {});
    this.onError = options.onError || (() => {});
    this.onComplete = options.onComplete || (() => {});
    this.apiBase = `${API_URL}/resilient-upload`;
    this.token = options.token;
    
    this.sessionUri = null; // The GCS Session URI
    this.gcsPath = null;    // The internal GCS path (upload_id)
    this.offset = 0;        // Current byte offset
    this.isAborted = false;
    
    // Session persistence key
    const prefix = options.prefix || 'upv2';
    this.sessionStorageKey = `${prefix}_gcs_${file.name}_${file.size}`;
  }

  normalizeRequestedContentType() {
    const fileType = String(this.file?.type || '').trim().toLowerCase();
    const fileName = String(this.file?.name || '').toLowerCase();
    const ext = fileName.includes('.') ? fileName.slice(fileName.lastIndexOf('.')) : '';
    const extMap = {
      '.mp4': 'video/mp4',
      '.webm': 'video/webm',
      '.mov': 'video/quicktime',
      '.qt': 'video/quicktime'
    };
    const extMime = extMap[ext];
    if (fileType === 'video/mov') return 'video/quicktime';
    if (fileType && fileType !== 'application/octet-stream') return fileType;
    if (extMime) return extMime;
    return 'video/mp4';
  }

  async start() {
    try {
      this.onStatus('Conectando ao Storage seguro...');
      
      // 1. Recover Session from localStorage
      const savedSession = localStorage.getItem(this.sessionStorageKey);
      if (savedSession) {
        const { sessionUri, gcsPath } = JSON.parse(savedSession);
        this.sessionUri = sessionUri;
        this.gcsPath = gcsPath;
        this.onStatus('Retomando sessão anterior...');
        await this.syncOffset(); // Check how many bytes GCS already has
      }

      // 2. Initialize new session if needed
      if (!this.sessionUri) {
        // A. Get Signed URL from our backend
        const requestedContentType = this.normalizeRequestedContentType();
        const initRes = await fetch(
          `${this.apiBase}/get-url?filename=${encodeURIComponent(this.file.name)}&type=${this.type}&contentType=${encodeURIComponent(requestedContentType)}`,
          {
          headers: { 'Authorization': `Bearer ${this.token}` }
          }
        );
        
        if (!initRes.ok) {
           let errMsg = 'Falha ao autorizar upload';
           try { const err = await initRes.json(); errMsg = err.message || err.detail || errMsg; } catch { /* non-JSON */ }
           throw new Error(errMsg);
        }
        
        const initPayload = await initRes.json();
        const { uploadUrl } = initPayload;
        const gcsPath = initPayload?.objectName || initPayload?.gcsPath;
        if (!uploadUrl || !gcsPath) {
          throw new Error('Resposta inválida do servidor de upload.');
        }
        this.gcsPath = gcsPath;
        const signedContentType = String(
          initPayload?.signedContentType || initPayload?.contentType || requestedContentType
        ).toLowerCase();
        const serverFileName = String(initPayload?.finalFileName || this.file?.name || '');
        const fileExtension = serverFileName.includes('.') ? serverFileName.slice(serverFileName.lastIndexOf('.')).toLowerCase() : '';
        const extExpected = (
          fileExtension === '.webm' ? 'video/webm'
          : fileExtension === '.mp4' ? 'video/mp4'
          : (fileExtension === '.mov' || fileExtension === '.qt') ? 'video/quicktime'
          : signedContentType
        );
        const uploadContentType = signedContentType;

        if (requestedContentType !== signedContentType || extExpected !== signedContentType) {
          console.error('[UPLOAD-V2] Content-Type mismatch detected. Aborting unsafe upload.', {
            requestedContentType,
            signedContentType,
            expectedFromExtension: extExpected,
            fileName: serverFileName || this.file?.name,
            fileSize: this.file?.size
          });
          throw new Error('Contrato de vídeo inválido: tipo do arquivo não confere com assinatura segura.');
        } else {
          console.info('[UPLOAD-V2] Content-Type OK', {
            requestedContentType,
            signedContentType,
            fileName: serverFileName || this.file?.name
          });
        }

        // B. Start Resumable Session with GCS
        const startHeaders = {
          'x-goog-resumable': 'start',
          'Content-Type': uploadContentType
        };
        console.info('[UPLOAD-V2] Starting GCS resumable session with headers:', startHeaders);
        const gcsRes = await fetch(uploadUrl, {
          method: 'POST',
          headers: startHeaders
        });

        if (!gcsRes.ok) {
          let gcsBody = '';
          try { gcsBody = await gcsRes.text(); } catch { /* non-text response */ }
          console.error('[UPLOAD-V2] GCS refused resumable start', {
            status: gcsRes.status,
            statusText: gcsRes.statusText,
            requestHeaders: startHeaders,
            responseBody: gcsBody?.slice?.(0, 600) || ''
          });
          throw new Error('GCS denied upload session start');
        }
        this.sessionUri = gcsRes.headers.get('Location');
        if (!this.sessionUri) {
          console.error('[UPLOAD-V2] Missing GCS Location header', { requestHeaders: startHeaders });
          throw new Error('Invalid upload session (missing Location header)');
        }
        
        // Save for persistence
        localStorage.setItem(this.sessionStorageKey, JSON.stringify({ 
          sessionUri: this.sessionUri, 
          gcsPath: this.gcsPath,
          contentType: uploadContentType
        }));
      }

      // 3. Upload loop
      while (this.offset < this.file.size) {
        if (this.isAborted) return;
        await this.uploadChunkWithRetry();
        
        const progress = Math.round((this.offset / this.file.size) * 90);
        this.onProgress(progress, `Enviando: ${Math.round(this.offset / 1024 / 1024)}MB de ${Math.round(this.file.size / 1024 / 1024)}MB`);
      }

      // 4. Finalize via Backend (Triggers Worker)
      this.onStatus('Sincronizando análise...');
      const completeRes = await fetch(`${this.apiBase}/complete`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          uploadId: this.gcsPath,
          type: this.type,
          game: this.game,
          language: this.language,
          locale: this.language,
          context: this.context,
          contentType: this.normalizeRequestedContentType(),
          fileName: this.file?.name || ''
        })
      });

      if (!completeRes.ok) {
        let errBody = {};
        try { errBody = await completeRes.json(); } catch { /* non-JSON body */ }
        const errCode = errBody?.error || errBody?.detail?.error || null;
        const errMsg = errBody?.message || errBody?.detail?.message || errBody?.detail || 'Falha ao registrar job de análise';
        const err = new Error(errMsg);
        err.code = errCode;
        err.status = completeRes.status;
        throw err;
      }

      let completeData = {};
      try { completeData = await completeRes.json(); } catch { /* non-JSON response */ }
      const jobId = completeData.jobId;
      if (!jobId) throw new Error('Servidor não retornou ID do job');
      
      localStorage.removeItem(this.sessionStorageKey);
      this.onComplete(jobId);

    } catch (error) {
      console.error('Uploader V2 Error:', error);
      this.onError(error.message || 'Erro crítico no upload.', error);
      throw error; // Re-throw to inform the caller
    }
  }

  /**
   * Syncs the current byte offset with GCS
   * Uses the GCS protocol for resumable upload status check
   */
  async syncOffset() {
    try {
      const res = await fetch(this.sessionUri, {
        method: 'PUT',
        headers: {
          'Content-Range': `bytes */${this.file.size}`
        }
      });

      if (res.status === 308) { // Permanent Redirect = Incomplete
         const range = res.headers.get('Range');
         if (range) {
           this.offset = parseInt(range.split('-')[1]) + 1;
         }
      } else if (res.ok || res.status === 201) {
         this.offset = this.file.size;
      }
    } catch (e) {
      console.warn('Sync failed, starting from 0');
    }
  }

  async uploadChunkWithRetry(retryCount = 0) {
    if (this.isAborted) return;

    try {
      const end = Math.min(this.file.size, this.offset + CHUNK_SIZE);
      const chunk = this.file.slice(this.offset, end);
      
      const res = await fetch(this.sessionUri, {
        method: 'PUT',
        headers: {
          'Content-Range': `bytes ${this.offset}-${end - 1}/${this.file.size}`
        },
        body: chunk
      });

      if (res.status === 308 || res.ok || res.status === 201) {
        this.offset = end;
      } else {
        throw new Error(`GCS Error: ${res.status}`);
      }
    } catch (error) {
      if (retryCount < MAX_RETRIES) {
        this.onStatus(`Otimizando conexão mobile (Tenta ${retryCount + 1})...`);
        await new Promise(r => setTimeout(r, 1000 * Math.pow(2, retryCount)));
        await this.syncOffset(); // Check actual status before retry
        return this.uploadChunkWithRetry(retryCount + 1);
      }
      throw error;
    }
  }

  abort() {
    this.isAborted = true;
  }
}

export default ResilientUploader;
